import { useQuery } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

export interface InboxConversation {
  id: string;
  contact_id: number;
  channel: "sms" | "email" | "call";
  subject: string | null;
  status: string;
  assigned_to: number | null;
  unread_count: number;
  last_message_at: string;
  last_inbound_at: string | null;
  snippet: string | null;
  contact_name: string;
  do_not_contact: boolean | null;
  assignee_name: string | null;
  awaiting_reply: boolean;
}

export type InboxFilter =
  | "all"
  | "unread"
  | "awaiting"
  | "mine"
  | "scheduled"
  | "closed";

export const FILTER_LABELS: Record<InboxFilter, string> = {
  all: "All",
  unread: "Unread",
  awaiting: "Awaiting reply",
  mine: "Assigned to me",
  scheduled: "Scheduled",
  closed: "Closed",
};

/**
 * The inbox list, from the `inbox_conversations` view.
 *
 * One query returns the contact name, assignee, unread count and last-message
 * snippet together. The previous implementation listed `conversations` and
 * then fetched contacts separately, which is the N+1 that makes a shared inbox
 * feel slow exactly when it is busiest.
 *
 * Filtering happens in SQL, not on the fetched page: "unread" must mean every
 * unread conversation, not the unread ones that happen to be in the first 200
 * rows.
 */
export const useInbox = (filter: InboxFilter) => {
  const workspaceId = getActiveWorkspaceId();
  const { identity } = useGetIdentity();
  const salesId = identity?.id;

  return useQuery<InboxConversation[]>({
    queryKey: ["inbox", workspaceId, filter, salesId],
    // The Scheduled tab renders its own surface, so this query is idle there.
    enabled: Boolean(workspaceId) && filter !== "scheduled",
    queryFn: async () => {
      let query = getSupabaseClient()
        .from("inbox_conversations")
        .select("*")
        .eq("workspace_id", workspaceId!)
        .order("last_message_at", { ascending: false })
        .limit(200);

      // Closed threads are hidden from every other filter, an inbox that
      // keeps showing what you already dealt with stops being a to-do list.
      if (filter === "closed") query = query.eq("status", "closed");
      else query = query.eq("status", "open");

      if (filter === "unread") query = query.gt("unread_count", 0);
      if (filter === "awaiting") query = query.eq("awaiting_reply", true);
      if (filter === "mine" && salesId != null)
        query = query.eq("assigned_to", salesId);

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []) as InboxConversation[];
    },
  });
};

export const markConversationRead = async (conversationId: string) => {
  await getSupabaseClient().rpc("mark_conversation_read", {
    p_conversation_id: conversationId,
  });
};
